import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ROLES, newPassword, type Role } from '@pulsepoint/shared';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useCreateUser, useOrganisationList, useSetTwoFactorRequired, useUpdateUser, type User } from '../../api/users';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { applyApiError } from '../../components/form/apiErrors';
import { FormTextField } from '../../components/form/FormTextField';
import { PasswordField } from '../../components/form/PasswordField';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { colours, radius } from '../../theme';
import { ROLE_INFO } from './roles';

const schema = z
  .object({
    displayName: z.string().trim().min(1, 'Enter their name').max(128),
    email: z.string().trim().email('Enter a valid email address').max(254),
    username: z
      .string()
      .trim()
      .max(64)
      .refine((v) => v === '' || /^[A-Za-z0-9._-]{2,}$/.test(v), 'At least 2 letters or numbers; dots, dashes and underscores are fine'),
    role: z.enum(ROLES),
    organisationIds: z.array(z.string()),
    passwordMode: z.enum(['link', 'temporary']),
    password: z.string(),
    sendWelcomeEmail: z.boolean(),
    mfaRequired: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.role !== 'admin' && v.organisationIds.length === 0) ctx.addIssue({ code: 'custom', path: ['organisationIds'], message: 'Choose at least one organisation' });
    if (v.passwordMode === 'temporary') {
      const r = newPassword.safeParse(v.password);
      if (!r.success) ctx.addIssue({ code: 'custom', path: ['password'], message: r.error.issues[0]!.message });
    }
  });
type Values = z.infer<typeof schema>;

/** Add a person, or edit one (pass `user`). Resolves with the saved person. */
export function UserDialog({ user, close }: { user?: User } & DialogProps<User>) {
  const editing = !!user;
  const create = useCreateUser();
  const update = useUpdateUser();
  const setTwoFactor = useSetTwoFactorRequired();
  const orgs = useOrganisationList();
  const toast = useToast();
  const [error, setError] = useState('');

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: user?.displayName ?? '',
      email: user?.email ?? '',
      username: user?.username ?? '',
      role: user?.role ?? 'user',
      organisationIds: user?.organisationIds ?? [],
      passwordMode: 'link',
      password: '',
      sendWelcomeEmail: true,
      mfaRequired: user?.mfaRequired ?? true,
    },
  });
  const role = useWatch({ control: form.control, name: 'role' });
  const displayName = useWatch({ control: form.control, name: 'displayName' });

  // New people: the username follows the first word of their name ("Jethro Smith" ->
  // "Jethro") until it's edited by hand. Usernames may be shared; the password decides.
  useEffect(() => {
    if (editing || form.getFieldState('username').isDirty) return;
    const first = (displayName ?? '').trim().split(/\s+/)[0] ?? '';
    form.setValue('username', first.replace(/[^A-Za-z0-9._-]/g, ''));
  }, [displayName, editing, form]);
  const passwordMode = useWatch({ control: form.control, name: 'passwordMode' });

  const submit = formSaver(form, async (v) => {
    setError('');
    const common = {
      displayName: v.displayName,
      email: v.email,
      role: v.role,
      organisationIds: v.role === 'admin' ? [] : v.organisationIds,
    };
    try {
      if (editing) {
        let saved = await update.mutateAsync({ id: user.id, ...common, username: v.username || null });
        if (v.mfaRequired !== user.mfaRequired) saved = await setTwoFactor.mutateAsync({ id: user.id, required: v.mfaRequired });
        toast(`${saved.displayName} was saved.`);
        close(saved);
      } else {
        const temporary = v.passwordMode === 'temporary';
        const saved = await create.mutateAsync({
          ...common,
          ...(v.username ? { username: v.username } : {}),
          ...(temporary ? { password: v.password } : {}),
          sendWelcomeEmail: temporary ? v.sendWelcomeEmail : true,
        });
        toast(
          saved.welcomeEmailSent
            ? `${saved.displayName} was added. We emailed them at ${saved.email}.`
            : `${saved.displayName} was added.${temporary ? ' Give them their temporary password.' : ''}`,
        );
        close(saved);
      }
    } catch (e) {
      setError(applyApiError(e, form.setError, ['displayName', 'email', 'username', 'role', 'organisationIds', 'password']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });

  return (
    <FormDialog
      title={editing ? `Edit ${user.displayName}` : 'Add a person'}
      lead={editing ? undefined : 'They will be able to sign in to Pulsepoint and see the organisations you choose.'}
      submitLabel={editing ? 'Save changes' : 'Add the person'}
      onSubmit={submit}
      onClose={() => close()}
      busy={create.isPending || update.isPending || setTwoFactor.isPending}
      error={error}
    >
      <FormTextField control={form.control} name="displayName" label="Name" autoFocus autoComplete="off" />
      <FormTextField control={form.control} name="email" label="Email" type="email" autoComplete="off" />
      <FormTextField
        control={form.control}
        name="username"
        label="Username (optional)"
        helperText="Users can sign in with this username or their email. Others may have the same username."
        autoComplete="off"
      />

      <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
        <Typography component="legend" sx={{ fontWeight: 500, mb: 1 }}>Role</Typography>
        <Controller
          control={form.control}
          name="role"
          render={({ field }) => (
            <RadioGroup {...field} sx={{ gap: 1 }}>
              {ROLES.slice().reverse().map((r: Role) => (
                <Box
                  key={r}
                  component="label"
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    p: 1.5,
                    pr: 2,
                    border: `1px solid ${field.value === r ? colours.nearBlack : colours.hairline}`,
                    borderRadius: `${radius.panel}px`,
                    cursor: 'pointer',
                  }}
                >
                  <Radio value={r} sx={{ p: 0.5, mt: -0.25 }} slotProps={{ input: { 'aria-label': ROLE_INFO[r].label, 'aria-describedby': `role-${r}-desc` } }} />
                  <Box>
                    <Typography sx={{ fontWeight: 500 }} aria-hidden>{ROLE_INFO[r].label}</Typography>
                    <Typography variant="body2" color="textSecondary" id={`role-${r}-desc`}>{ROLE_INFO[r].description}</Typography>
                  </Box>
                </Box>
              ))}
            </RadioGroup>
          )}
        />
      </Box>

      {role !== 'admin' && (
        <Controller
          control={form.control}
          name="organisationIds"
          render={({ field, fieldState }) => (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={orgs.data ?? []}
              loading={orgs.isPending}
              getOptionLabel={(o) => o.businessName}
              value={(orgs.data ?? []).filter((o) => field.value.includes(o.id))}
              onChange={(_e, value) => field.onChange(value.map((o) => o.id))}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Organisations they can see" error={!!fieldState.error} helperText={fieldState.error?.message} inputRef={field.ref} />
              )}
            />
          )}
        />
      )}

      {editing && (
        <Controller
          control={form.control}
          name="mfaRequired"
          render={({ field }) => (
            <Box>
              <FormControlLabel control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Two-factor sign-in" />
              <Typography variant="body2" color={!field.value && user.mfaRequired ? colours.faultRed : 'textSecondary'} sx={{ ml: 6 }}>
                {field.value
                  ? user.mfaRequired
                    ? user.mfaEnabledAt
                      ? 'A code from their authenticator app as well as their password.'
                      : 'They set up their authenticator app at their next sign-in.'
                    : 'On saving, they set up their authenticator app at their next sign-in.'
                  : user.mfaRequired
                    ? 'On saving, their password alone signs them in. Their authenticator app and remembered devices are forgotten; turned on again, they set it up again.'
                    : 'Off: their password alone signs them in.'}
              </Typography>
            </Box>
          )}
        />
      )}

      {!editing && (
        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
          <Typography component="legend" sx={{ fontWeight: 500, mb: 1 }}>Their password</Typography>
          <Controller
            control={form.control}
            name="passwordMode"
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel value="link" control={<Radio />} label="Email them a link to choose their own (recommended)" />
                <FormControlLabel value="temporary" control={<Radio />} label="Set a temporary password to give them" />
              </RadioGroup>
            )}
          />
          {passwordMode === 'temporary' && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <PasswordField control={form.control} name="password" label="Temporary password" autoComplete="new-password" onGenerate={(p) => form.setValue('password', p, { shouldDirty: true, shouldValidate: true })} helperText="At least 10 characters. They must change it when they first sign in." />
              <Controller
                control={form.control}
                name="sendWelcomeEmail"
                render={({ field }) => (
                  <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Also send a welcome email (without the password)" />
                )}
              />
            </Stack>
          )}
        </Box>
      )}
    </FormDialog>
  );
}
